import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { TrackedLink } from "@/components/analytics/TrackedLink";
import { TrackedAnchor } from "@/components/analytics/TrackedAnchor";
import { MEMBER_LOGIN_URL, CREATOR_LOGIN_URL } from "@/constants/urls";

interface MobileMenuProps {
  /** When provided, renders a "Get the App" primary item that closes the
   *  menu and opens the store-badges overlay. */
  onGetTheApp?: () => void;
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Full-screen mobile menu. Large "Become a Member" / "Become a Creator" sign-up
 * links sit in the center; small "Member login" / "Creator login" links sit
 * side-by-side at the bottom. The morphing hamburger/X toggle (in SiteHeader,
 * stacked above this overlay) is the close affordance. Rendered through a portal
 * to document.body so it is not clamped by the header's backdrop-filter
 * containing block. Locks body scroll, traps Tab focus, closes on Escape, and
 * returns focus to the opener on close.
 */
const MobileMenu = ({ isOpen, onClose, onGetTheApp }: MobileMenuProps) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const opener = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key === "Tab") {
        const focusables = containerRef.current?.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
        );
        if (!focusables || focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener("keydown", onKeyDown);
    containerRef.current?.querySelector<HTMLElement>("a[href], button")?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
      opener?.focus?.();
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div
      ref={containerRef}
      role="dialog"
      aria-modal="true"
      aria-label="Menu"
      className="fixed inset-0 z-[60] md:hidden flex flex-col bg-black/90 text-white px-6 pb-8 animate-in fade-in duration-200"
    >
      {/* Reserve space for the header (logo + morphing X live there) */}
      <div className="h-16 shrink-0" aria-hidden="true" />

      {/* Primary — large sign-up links, centered */}
      <div className="flex-1 flex flex-col items-center justify-center gap-9 text-center">
        <TrackedLink
          ctaId="become_a_member"
          ctaLocation="mobile_menu"
          to="/become-a-member"
          onClick={onClose}
          className="text-[1.75rem] font-black italic uppercase leading-[1.02] tracking-tight text-white transition-colors hover:text-cyan focus-visible:outline-none focus-visible:text-cyan"
        >
          Become a Member
        </TrackedLink>
        <TrackedLink
          ctaId="become_a_creator"
          ctaLocation="mobile_menu"
          to="/become-a-creator"
          onClick={onClose}
          className="text-[1.75rem] font-black italic uppercase leading-[1.02] tracking-tight text-white transition-colors hover:text-lime-burst focus-visible:outline-none focus-visible:text-lime-burst"
        >
          Become a Creator
        </TrackedLink>
        {onGetTheApp && (
          <button
            type="button"
            onClick={onGetTheApp}
            className="text-[1.75rem] font-black italic uppercase leading-[1.02] tracking-tight text-cyan transition-colors hover:text-white focus-visible:outline-none focus-visible:text-white"
          >
            Get the App
          </button>
        )}
      </div>

      {/* Secondary — small login links, side-by-side */}
      <div className="flex items-center justify-center gap-5 text-sm text-zinc-400">
        <TrackedAnchor
          ctaId="login_member"
          ctaLocation="mobile_menu"
          href={MEMBER_LOGIN_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onClose}
          className="underline-offset-4 transition-colors hover:text-white hover:underline focus-visible:outline-none focus-visible:text-white"
        >
          Member login
        </TrackedAnchor>
        <span aria-hidden="true" className="h-4 w-px bg-white/20" />
        <TrackedAnchor
          ctaId="login_creator"
          ctaLocation="mobile_menu"
          href={CREATOR_LOGIN_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onClose}
          className="underline-offset-4 transition-colors hover:text-white hover:underline focus-visible:outline-none focus-visible:text-white"
        >
          Creator login
        </TrackedAnchor>
      </div>
    </div>,
    document.body,
  );
};

export default MobileMenu;
